import type {
	RingCentralDialErrorCode,
	RingCentralUserDeviceDto,
	RingCentralUserDevicesResponseDto,
} from '@supply-mover-2-0/shared';
import { formatPhoneNumber } from './formatters';

/**
 * Decisions behind the RingCentral dial dialog (PD-16786), kept free of React
 * so they can be unit-tested.
 *
 * Background: RingCentral's Call Out can only ring a desk phone, an existing
 * SIP phone or the legacy RingCentral Phone softphone. The current RingCentral
 * App cannot be dialed directly, so its users have to call with RingOut, which
 * rings their own phone first and then connects the call.
 */

export type RingCentralDialMethod = 'callout' | 'ringout';

/**
 * What the dialog shows once the user's phones have been looked up.
 *
 * - reconnect: the RingCentral sign-in expired or was revoked; nothing works
 *   until the user connects again, whichever method they pick.
 * - permissionDenied: RingCentral refused to list the user's phones; only
 *   their RingCentral administrator can fix it. RingOut may still work.
 * - unavailable: RingCentral could not be reached; trying again may work.
 * - noCallOutDevice: nothing Call Out can ring. `appOnly` says the user has
 *   the RingCentral App, which cannot be dialed directly.
 * - devices: Call Out capable phones, the ones that can be rung right now
 *   first. `hasOnlineDevice` is false when every one of them is offline.
 */
export type RingCentralDeviceViewModel =
	| { kind: 'reconnect'; message: string }
	| { kind: 'permissionDenied'; message: string }
	| { kind: 'unavailable'; message: string }
	| { kind: 'noCallOutDevice'; appOnly: boolean }
	| {
			kind: 'devices';
			devices: Array<RingCentralUserDeviceDto>;
			hasOnlineDevice: boolean;
	  };

/**
 * The phones Call Out can ring, ready-to-ring ones first. Offline desk phones
 * stay in the list (shown but not selectable) so the user can see why their
 * phone is not offered.
 */
export function getCallOutDevices(
	devices: Array<RingCentralUserDeviceDto>,
): Array<RingCentralUserDeviceDto> {
	const capable = devices.filter((device) => device.callOutCapable);

	return [
		...capable.filter((device) => device.supportsCallOut),
		...capable.filter((device) => !device.supportsCallOut),
	];
}

export function getRingCentralDeviceView(
	result: RingCentralUserDevicesResponseDto,
): RingCentralDeviceViewModel {
	if (result.error != null) {
		if (result.error.code === 'RECONNECT_REQUIRED') {
			return { kind: 'reconnect', message: result.error.message };
		}

		if (result.error.code === 'PERMISSION_DENIED') {
			return { kind: 'permissionDenied', message: result.error.message };
		}

		return { kind: 'unavailable', message: result.error.message };
	}

	const devices = getCallOutDevices(result.devices);

	if (devices.length === 0) {
		return { kind: 'noCallOutDevice', appOnly: result.appOnly };
	}

	return {
		kind: 'devices',
		devices,
		hasOnlineDevice: devices.some((device) => device.supportsCallOut),
	};
}

/**
 * The method the dialog opens on.
 *
 * A remembered choice always wins. Otherwise it is automatic: Call Out when
 * there is a phone it can ring right now, RingOut when there is not (App-only
 * users, offline desk phones, or RingCentral refusing to list phones). When
 * the lookup failed outright the dialog stays on Call Out so the problem and
 * its "Try again" stay visible instead of being hidden behind RingOut.
 */
export function chooseDefaultDialMethod(
	remembered: RingCentralDialMethod | null,
	view: RingCentralDeviceViewModel,
): RingCentralDialMethod {
	if (remembered != null) {
		return remembered;
	}

	if (view.kind === 'devices') {
		return view.hasOnlineDevice ? 'callout' : 'ringout';
	}

	if (view.kind === 'noCallOutDevice' || view.kind === 'permissionDenied') {
		return 'ringout';
	}

	return 'callout';
}

/**
 * The phone preselected for Call Out: the remembered one when it can be rung
 * right now, otherwise the first one that can. Null when none can.
 */
export function pickInitialCallOutDevice(
	devices: Array<RingCentralUserDeviceDto>,
	rememberedDeviceId: string | null,
): RingCentralUserDeviceDto | null {
	const callable = devices.filter((device) => device.supportsCallOut);

	if (rememberedDeviceId != null) {
		const remembered = callable.find(
			(device) => device.deviceId === rememberedDeviceId,
		);

		if (remembered != null) {
			return remembered;
		}
	}

	return callable.at(0) ?? null;
}

/**
 * The RingOut "ring me first" number to prefill: the number remembered in this
 * browser, otherwise the user's own phone number from their Workd profile.
 */
export function getInitialRingOutPhone(
	rememberedPhone: string | null,
	userPhoneNumber: string | null,
): string {
	if (rememberedPhone != null && rememberedPhone.trim() !== '') {
		return rememberedPhone;
	}

	if (userPhoneNumber != null && userPhoneNumber.trim() !== '') {
		return formatPhoneNumber(userPhoneNumber, { valueIfEmpty: '' });
	}

	return '';
}

export interface RingCentralDialFailureActionModel {
	/** Ask the user to connect RingCentral again */
	reconnect: boolean;
	/** Reopen the dial dialog so the user can pick another phone or number */
	reopenDialog: boolean;
	/** Forget the remembered Call Out phone - RingCentral no longer knows it */
	forgetDevice: boolean;
}

/**
 * What to do after a dial fails, beyond showing the error.
 */
export function getDialFailureAction(
	errorCode: RingCentralDialErrorCode | undefined,
): RingCentralDialFailureActionModel {
	switch (errorCode) {
		case 'RECONNECT_REQUIRED':
			return { reconnect: true, reopenDialog: false, forgetDevice: false };

		case 'DEVICE_NOT_FOUND':
			return { reconnect: false, reopenDialog: true, forgetDevice: true };

		case 'DEVICE_REQUIRED':
		case 'NUMBER_REQUIRED':
		case 'INVALID_NUMBER':
			return { reconnect: false, reopenDialog: true, forgetDevice: false };

		default:
			return { reconnect: false, reopenDialog: false, forgetDevice: false };
	}
}
